/* eslint-disable import/no-cycle */
/* eslint-disable no-restricted-globals */
import React, { useCallback, useMemo } from 'react';
import { Select, Text } from '@chakra-ui/core';
import { useTranslation } from 'react-i18next';
import { QuestionnaireTemplateStore } from '../../../../store/questionnaireTemplateModel';
import { VALIDATION_CONSTRAINTS } from './questionValidationRules.constraints';
import { INLINE_SELECT_PROPS } from './questionValidationRules.style';
import type { OptionSelectionCount, ValidationOperator } from './questionValidationRules.types';
import { operatorRequiresMinCount } from './questionValidationRules.utils';

const INITIAL_OPTION_COUNT = 0;

interface QuestionValidationMinCountSelectProps {
  elementId: string;
  operator: ValidationOperator;
  selectionCount: OptionSelectionCount;
  minCount: number | undefined;
  onChange: (newMinCount: number) => void;
}

export const QuestionValidationMinCountSelect = React.memo<QuestionValidationMinCountSelectProps>(
  function QuestionValidationMinCountSelect({ elementId, operator, selectionCount, minCount, onChange }) {
    const { t } = useTranslation('questionnaireTemplate');

    const optionsCount = QuestionnaireTemplateStore.useStoreState(
      (s) => s.entities.elements[elementId]?.options?.length ?? INITIAL_OPTION_COUNT,
    );

    const showMinCount = useMemo(() => operatorRequiresMinCount(operator, t), [operator, t]);

    const minCountOptions = useMemo(() => {
      if (!showMinCount) return [];
      const max = VALIDATION_CONSTRAINTS.AT_LEAST.minCountMax(selectionCount, optionsCount);
      return Array.from({ length: max }, (_, i) => i + VALIDATION_CONSTRAINTS.AT_LEAST.minCountMin);
    }, [showMinCount, selectionCount, optionsCount]);

    const handleChange = useCallback(
      (e: React.ChangeEvent<HTMLSelectElement>) => {
        const value = parseInt(e.target.value, 10);
        /* istanbul ignore else */
        if (!isNaN(value)) onChange(value);
      },
      [onChange],
    );

    if (!showMinCount) return null;

    const controlledValue = minCount ?? VALIDATION_CONSTRAINTS.AT_LEAST.minCountMin;

    return (
      <>
        <Select
          data-testid="min-count"
          display="inline-block"
          {...INLINE_SELECT_PROPS}
          value={controlledValue}
          onChange={handleChange}
        >
          {minCountOptions.map((n) => (
            <option key={n} value={n}>
              {n}
            </option>
          ))}
        </Select>
        <Text as="span" fontSize="sm" color="gray.700" mr={2} ml={1}>
          {t('formBuilder.canvas.element.validationRules.of')}
        </Text>
      </>
    );
  },
);
